/* ──────────────────────────────────────────────────────────────────────────
   co-klaviyo-forms.js — sends Clean Otter's contact-form submissions to Klaviyo

   Why this exists
   Shopify's native contact form only emails the store. It does not create a
   customer, and neither Shopify Flow nor Klaviyo can see the submission. So
   the confirmation emails the forms promise ("A confirmation email is on its
   way") have nothing to trigger them. This script records each submission as a
   Klaviyo event, and the Klaviyo flows send the emails from that event.

   How it works (it never blocks or delays the real submit)
   1. On submit, it reads the contact[...] fields and puts them in a small
      queue in sessionStorage, marked "pending".
   2. Shopify redirects back with ?contact_posted=true only when the submission
      was accepted. On that page load, pending items are marked "confirmed".
      A submission that failed validation never gets that parameter, so it is
      dropped and no email goes out for it.
   3. Confirmed items are sent to Klaviyo's Client API (public key only).
      If the call fails, the item stays queued and is retried on the next page.

   Scope
   Contact forms only (form_type = contact). Newsletter signups and the buyer
   application are customer forms; they reach Klaviyo through the Shopify
   integration and are left alone here.

   Settings come from the <script> tag in layout/theme.liquid:
     data-klaviyo-key     Klaviyo public API key (Site ID). Leave empty to read
                          it from the Klaviyo app embed's script at runtime.
     data-logged-in       "true" when a customer is logged in
     data-customer-*      first name, last name, company, country code
   ────────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var SELF = document.currentScript;
  var CFG = {
    key: (SELF && SELF.dataset.klaviyoKey) || '',
    loggedIn: !!(SELF && SELF.dataset.loggedIn === 'true'),
    customer: {
      first_name: (SELF && SELF.dataset.customerFirst) || '',
      last_name: (SELF && SELF.dataset.customerLast) || '',
      company: (SELF && SELF.dataset.customerCompany) || '',
      country: (SELF && SELF.dataset.customerCountry) || ''
    }
  };

  var QUEUE_KEY = 'co-kl-queue';
  var REVISION = '2026-07-15';
  var PENDING_TTL = 30 * 60 * 1000;          // a submit must be confirmed within 30 min
  var CONFIRMED_TTL = 48 * 60 * 60 * 1000;   // retry confirmed sends for up to 2 days
  var MAX_ATTEMPTS = 5;

  /* request_type (or tag) → Klaviyo metric name. The flows trigger on these
     exact names, so change them in both places or not at all. */
  var METRICS = {
    brand_application: 'Submitted Brand Application',
    sample: 'Submitted Sample Request',
    distributor: 'Submitted Distributor Pricing Request',
    curated: 'Submitted Curated Selection Request',
    credit: 'Submitted Credit Request',
    services: 'Submitted Services Enquiry',
    brand_request: 'Submitted Brand Request',
    brand_review: 'Submitted Brand Review',
    exclusivity: 'Submitted Exclusivity Request',
    no_vat: 'Submitted No-VAT Request',
    help: 'Submitted Help Question'
  };
  var FALLBACK_METRIC = 'Submitted Contact Form';

  /* Fields that are routing plumbing rather than content. */
  var SKIP = { tags: 1, email: 1, request_type: 1 };

  /* ── storage ─────────────────────────────────────────────────────────── */
  function readQueue() {
    try { return JSON.parse(sessionStorage.getItem(QUEUE_KEY) || '[]') || []; }
    catch (_) { return []; }
  }
  function writeQueue(q) {
    try { sessionStorage.setItem(QUEUE_KEY, JSON.stringify(q)); } catch (_) {}
  }

  /* ── capture on submit ───────────────────────────────────────────────── */
  function isContactForm(form) {
    var ft = form.querySelector('input[name="form_type"]');
    return !!(ft && ft.value === 'contact');
  }

  function fieldsOf(form) {
    var out = {};
    var els = form.elements;
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var m = el.name && el.name.match(/^contact\[([^\]]+)\]$/);
      if (!m || el.disabled) continue;
      if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) continue;
      var v = (el.value || '').trim();
      if (!v) continue;
      /* Same name twice (e.g. a hidden brand plus a visible brand field):
         the later, visible one wins, as it does in Shopify's own email. */
      out[m[1]] = v;
    }
    return out;
  }

  function typeOf(f) {
    if (f.request_type) return f.request_type;
    var t = (f.tags || '').toLowerCase();
    var m = t.match(/request:([a-z_]+)/);
    return m ? m[1] : '';
  }

  function makeRef() {
    var t = Date.now().toString(36).toUpperCase();
    var r = Math.random().toString(36).slice(2, 5).toUpperCase();
    return 'CO-' + t.slice(-5) + r;
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM' || !isContactForm(form)) return;

    var f = fieldsOf(form);
    if (!f.email) return; // Shopify will reject it anyway

    var type = typeOf(f);
    var metric = METRICS[type] || FALLBACK_METRIC;

    var props = { request_type: type || 'contact', ref: makeRef() };
    Object.keys(f).forEach(function (k) {
      if (SKIP[k]) return;
      props[k === 'body' ? 'message' : k] = f[k];
    });
    /* Full URL: the back-in-stock button lands on the help centre with
       ?topic=back-in-stock&product=…, which the team needs to see. */
    props.page_url = location.href.split('#')[0];
    props.page_title = document.title;
    props.logged_in = CFG.loggedIn;
    if (CFG.loggedIn && CFG.customer.company && !props.company) props.company = CFG.customer.company;
    if (CFG.customer.country) props.customer_country = CFG.customer.country;

    var item = {
      status: 'pending',
      ts: Date.now(),
      attempts: 0,
      metric: metric,
      email: f.email,
      first_name: f.first_name || CFG.customer.first_name,
      last_name: f.last_name || CFG.customer.last_name,
      organization: f.brand_name || f.company || CFG.customer.company,
      title: f.role || '',
      phone: f.phone || '',
      props: props
    };

    /* A resubmit after a validation error replaces the earlier attempt, so
       one accepted submission never produces two events. */
    var q = readQueue().filter(function (x) {
      return !(x.status === 'pending' && x.metric === metric);
    });
    q.push(item);
    writeQueue(q);
  }, false); // bubbling and passive: never calls preventDefault

  /* ── confirm on the redirect back ────────────────────────────────────── */
  function confirmPending() {
    var posted = /[?&]contact_posted=true\b/.test(location.search);
    var now = Date.now();
    var q = readQueue().filter(function (x) {
      if (x.status === 'pending') return now - x.ts < PENDING_TTL;
      return now - x.ts < CONFIRMED_TTL && x.attempts < MAX_ATTEMPTS;
    });
    if (posted) q.forEach(function (x) { if (x.status === 'pending') x.status = 'confirmed'; });
    writeQueue(q);
    return q.filter(function (x) { return x.status === 'confirmed'; });
  }

  /* ── Klaviyo public key ──────────────────────────────────────────────── */
  function findKey() {
    if (CFG.key) return CFG.key;
    var scripts = document.querySelectorAll('script[src*="klaviyo"], script[data-src*="klaviyo"]');
    for (var i = 0; i < scripts.length; i++) {
      var src = scripts[i].getAttribute('src') || scripts[i].getAttribute('data-src') || '';
      var m = src.match(/company_id=([A-Za-z0-9]+)/) || src.match(/onsite\/js\/([A-Za-z0-9]{6})\//);
      if (m) return (CFG.key = m[1]);
    }
    return '';
  }

  /* ── send ────────────────────────────────────────────────────────────── */
  function payload(x) {
    var profile = { email: x.email };
    if (x.first_name) profile.first_name = x.first_name;
    if (x.last_name) profile.last_name = x.last_name;
    if (x.organization) profile.organization = x.organization;
    if (x.title) profile.title = x.title;
    /* Klaviyo rejects phone numbers that are not E.164, which would drop the
       whole event, so anything else stays an event property only. */
    var phone = (x.phone || '').replace(/[\s().-]/g, '');
    if (/^\+[1-9]\d{6,14}$/.test(phone)) profile.phone_number = phone;
    else if (x.phone) x.props.phone = x.phone;

    return {
      data: {
        type: 'event',
        attributes: {
          properties: x.props,
          unique_id: x.props.ref,
          metric: { data: { type: 'metric', attributes: { name: x.metric } } },
          profile: { data: { type: 'profile', attributes: profile } }
        }
      }
    };
  }

  function send(x, key) {
    return fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(key), {
      method: 'POST',
      keepalive: true,
      headers: {
        accept: 'application/vnd.api+json',
        'content-type': 'application/vnd.api+json',
        revision: REVISION
      },
      body: JSON.stringify(payload(x))
    }).then(function (res) { return res.ok; }, function () { return false; });
  }

  function flush(tries) {
    var ready = confirmPending();
    if (!ready.length) return;

    var key = findKey();
    if (!key) {
      /* The Klaviyo embed (or the cookie banner) may inject its script late. */
      if (tries < 10) setTimeout(function () { flush(tries + 1); }, 500);
      return;
    }

    ready.forEach(function (x) {
      send(x, key).then(function (ok) {
        var q = readQueue();
        q = q.filter(function (y) {
          if (y.props.ref !== x.props.ref) return true;
          if (ok) return false;           // delivered: drop it
          y.attempts = (y.attempts || 0) + 1;
          return true;                    // failed: retry on the next page
        });
        writeQueue(q);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { flush(0); });
  } else {
    flush(0);
  }
})();
